import {
  MonitoringRights,
  CouponType,
  AntiDilutionRights,
  LiquidationPreferenceType,
} from 'further-types/lp/holding-class';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { isMongoId, withSortableParams } from '../../helpers';
import { z } from 'zod';

const BaseHoldingClassInvestmentSchema = z.object({
  name: z
    .string({
      required_error: 'Class name is required',
    })
    .min(1, 'Class name is required'),
  investmentAmountTcy: z.number({
    required_error: 'Investment amount is required',
  }),
  numberOfUnits: z.number({
    required_error: 'Number of units is required',
  }),
  unitPriceTcy: z.number({
    required_error: 'Unit price is required',
  }),
  currency: z.nativeEnum(CurrencyCode, {
    errorMap: () => ({ message: 'Currency is required' }),
  }),
});

export const CoreHoldingClassSchema = z.object({
  //need to sort this out
  name: z
    .string({
      required_error: 'Class name is required',
    })
    .min(1, 'Class name is required'),
  holdingClassId: z.string().optional(),
  _id: z.string().optional(),
  assetId: z
    .string()
    .refine(isMongoId(), {
      message: 'Asset ID is required',
    })
    .optional(),
  currency: z.nativeEnum(CurrencyCode, {
    errorMap: () => ({ message: 'Currency is required' }),
  }),
  monitoringRights: z
    .nativeEnum(MonitoringRights, {
      errorMap: () => ({
        message: 'Monitoring rights is required',
      }),
    })
    .optional(),
  liquidationPreferenceType: z
    .nativeEnum(LiquidationPreferenceType, {
      errorMap: () => ({
        message: 'Liquidation preference type is required',
      }),
    })
    .optional(),
  liquidationPreferenceMultiple: z.number().optional(),
  antiDilutionRights: z
    .nativeEnum(AntiDilutionRights, {
      errorMap: () => ({
        message: 'Anti-dilution rights is required',
      }),
    })
    .optional(),
  couponRights: z
    .nativeEnum(CouponType, {
      errorMap: () => ({ message: 'Coupon rights is required' }),
    })
    .optional(),
  couponAmount: z.number().optional().nullable(),
});

export const NewHoldingClassSchema = BaseHoldingClassInvestmentSchema.extend({
  isNewHoldingClass: z.literal(true),
  ...CoreHoldingClassSchema.shape,
});

const ExistingHoldingClassSchema = BaseHoldingClassInvestmentSchema.extend({
  isNewHoldingClass: z.literal(false),
  holdingClassId: z.string().refine(isMongoId(), {
    message: 'Please select an existing holding class or create a new one',
  }),
});

export const HoldingClassSchema = z.discriminatedUnion('isNewHoldingClass', [
  NewHoldingClassSchema,
  ExistingHoldingClassSchema,
]);

export const ListAll = withSortableParams(
  z.object({
    search: z.string().optional().nullable(),
    assetId: z.string().refine(isMongoId()),
  }),
  [''],
);

export const CreateHoldingClassParamsSchema = z.object({
  assetId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  holdingClass: CoreHoldingClassSchema,
});

export const DeleteHoldingClassParamsSchema = z.object({
  id: z.string().refine(isMongoId()),
  assetId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
});

export const UpdateHoldingClassParamsSchema = z.object({
  vehicleId: z.string().refine(isMongoId()),
  holdingClass: CoreHoldingClassSchema,
});

export type HoldingClassFormValues = z.infer<typeof HoldingClassSchema>;

export type CoreHoldingClass = z.infer<typeof CoreHoldingClassSchema>;
